import React from "react";
import { AbsoluteFill, Img, OffthreadVideo, Sequence, useVideoConfig } from "remotion";

/**
 * Brand layer (etap E): a small logo in the top-right safe zone for the whole clip, and an
 * optional outro card / video appended after the last spoken word (silent). Shared by the
 * server render and the panel preview.
 */
export interface BrandOverlayProps {
  /** Data URL or https URL of the logo; null hides it. */
  logoUrl?: string | null;
  /** Logo width as a fraction of the frame width. */
  logoWidthRatio?: number;
  logoOpacity?: number;
}

export interface OutroProps {
  url: string;
  kind: "image" | "video";
  durationMs: number;
}

export const BrandLogo: React.FC<BrandOverlayProps> = ({ logoUrl, logoWidthRatio = 0.2, logoOpacity = 0.85 }) => {
  const { width, height } = useVideoConfig();
  if (!logoUrl) return null;
  const logoWidth = Math.round(width * logoWidthRatio);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <Img
        src={logoUrl}
        style={{
          position: "absolute",
          top: Math.round(height * 0.05),
          right: Math.round(width * 0.06),
          width: logoWidth,
          opacity: logoOpacity,
          filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.45))",
        }}
      />
    </AbsoluteFill>
  );
};

/** Outro after `fromMs` on the output timeline: a still card or a short video, full frame. */
export const BrandOutro: React.FC<{ outro: OutroProps | null | undefined; fromMs: number }> = ({ outro, fromMs }) => {
  const { fps } = useVideoConfig();
  if (!outro) return null;
  const from = Math.round((fromMs / 1000) * fps);
  const durationInFrames = Math.max(1, Math.round((outro.durationMs / 1000) * fps));
  return (
    <Sequence from={from} durationInFrames={durationInFrames}>
      <AbsoluteFill style={{ backgroundColor: "black" }}>
        {outro.kind === "video" ? (
          <OffthreadVideo src={outro.url} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Img src={outro.url} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )}
      </AbsoluteFill>
    </Sequence>
  );
};
